import { ActionButton } from "@via/components/action-button/action-button.tsx";
import { GenericError } from "@via/components/error.tsx";
import { SkeletonLoader } from "@via/components/loading/loading.tsx";
import { PageHeading } from "@via/components/page-heading.tsx";
import IndependentPagination from "@via/components/pagination.tsx";
import useUrlParams from "@via/hooks/useURLParams";
import CreatePipelineModal from "@via/pages/pipelines/create-pipeline/create-pipeline-modal.tsx";
import PipelineFilter from "@via/pages/pipelines/pipeline-filter.tsx";
import PipelinesTabBar from "@via/pages/pipelines/pipelines-tab-bar.tsx";
import PipelineGlossary from "@via/pages/pipelines/pipeline-glossary.tsx";
import PipelineTable from "@via/pages/pipelines/pipeline-table.tsx";
import { useViewMode } from "@via/pages/pipelines/view-mode.ts";
import ViewModeToggle from "@via/pages/pipelines/view-mode-toggle.tsx";
import {
  usePipelineList,
  usePipelineMenuGroupsList,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import { BUTTONS, ERROR, PAGE_NAMES, ROUTES } from "@via/utils/constants.tsx";
import { SelectOption } from "@via/utils/interfaces.tsx";
import { MenuGroupListItem, PipelineViewType } from "common";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ImportViaPipelineModal from "@via/pages/projects/pipelines/workflow/components/import-pipeline/import-via-pipeline-modal";
import ImportPipelineFromGitModal from "@via/pages/pipelines/import-pipeline-from-git/import-pipeline-from-git-modal.tsx";
import { useRegisterCortexContext } from "@via/hooks/useRegisterCortexContext";

const pageSizes = [10, 20, 100];
const defaultParams = {
  page: 0,
  pageSize: 100,
  filters: [
    {
      id: "pipelineType",
      value: [PipelineViewType.Released],
    },
    {
      id: "userNameText",
      value: "",
    },
    {
      id: "searchText",
      value: "",
    },
  ],
};

export default function PipelinesList() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUrlParams, getInitialParams } = useUrlParams(defaultParams);
  const initialParams = getInitialParams();
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [selectedPipelineType, setSelectedPipelineType] = useState<
    PipelineViewType[]
  >(initialParams.filters[0].value);
  const [userNameText, setUserNameText] = useState(
    initialParams.filters[1].value
  );
  const [searchText, setSearchText] = useState(initialParams.filters[2].value);
  const {
    data: pipelinesList,
    isLoading,
    isError,
  } = usePipelineList(
    page,
    pageSize,
    selectedPipelineType,
    searchText,
    userNameText
  );
  const [viewMode, setViewMode] = useViewMode("via.pipelines.view");
  const [isCreatePipelineModalOpen, setIsCreatePipelineModalOpen] =
    useState(false);
  const [isImportViaPipelineModalOpen, setIsImportViaPipelineModalOpen] =
    useState(false);
  const [isImportFromGitModalOpen, setIsImportFromGitModalOpen] =
    useState(false);
  const [menuGroups, setMenuGroups] = useState<SelectOption[]>([]);

  const { data: menuGroupsListData, isSuccess: isMenuGroupsListSuccess } =
    usePipelineMenuGroupsList();

  useEffect(() => {
    const newPipeline = searchParams.get("new");
    if (newPipeline) {
      setIsCreatePipelineModalOpen(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!isCreatePipelineModalOpen) {
      searchParams.delete("new");
      navigate({
        pathname: "/pipelines",
        search: searchParams.toString(),
      });
    }
  }, [isCreatePipelineModalOpen]);

  useEffect(() => {
    if (menuGroupsListData) {
      const groups = menuGroupsListData.data.map((mg: MenuGroupListItem) => {
        return { label: mg.name, value: mg.id };
      });
      setMenuGroups(groups);
    }
  }, [isMenuGroupsListSuccess]);

  useEffect(() => {
    setUrlParams({
      page,
      pageSize,
      filters: [
        { id: "pipelineType", value: selectedPipelineType },
        { id: "userNameText", value: userNameText },
        { id: "searchText", value: searchText },
      ],
    });
  }, [page, pageSize, selectedPipelineType, searchText, userNameText]);

  const getPipelinesListContext = useCallback(() => {
    const pipelines = pipelinesList?.data || [];

    return {
      pipelines: pipelines.map(pipeline => ({
        id: pipeline.id,
        name: pipeline.name,
        description: pipeline.description,
        owner: pipeline.ownerName,
        createdAt: pipeline.createdAt,
        updatedAt: pipeline.updatedAt,
        pipelineType: pipeline.pipelineType,
      })),
      total: pipelinesList?.total || 0,
      currentPage: page,
      pageSize: pageSize,
      filters: [
        { id: "pipelineType", value: selectedPipelineType },
        { id: "userNameText", value: userNameText },
        { id: "searchText", value: searchText },
      ],
    };
  }, [
    pipelinesList?.data,
    pipelinesList?.total,
    page,
    pageSize,
    selectedPipelineType,
    userNameText,
    searchText,
  ]);

  useRegisterCortexContext({
    routePattern: ROUTES.PIPELINES,
    contextName: "Pipelines List",
    getContext: getPipelinesListContext,
    systemPrompt:
      "You are analyzing pipelines list page. Help answer questions about the pipelines list page.",
  });

  return (
    <main className="via-content-wrapper">
      <div className="via-page-heading-wrapper">
        <PageHeading title={PAGE_NAMES.PIPELINES} />
        <div className="flex gap-2">
          <ActionButton
            id="importPipelineFromGit"
            button="Import from Git"
            primary={false}
            onClick={() => setIsImportFromGitModalOpen(true)}
          />
          <ActionButton
            button={BUTTONS.CREATE_PIPELINE}
            primary={true}
            onClick={() => setIsCreatePipelineModalOpen(true)}
          />
        </div>
      </div>
      <PipelinesTabBar active="pipelines" />
      {isImportFromGitModalOpen && (
        <ImportPipelineFromGitModal
          open={isImportFromGitModalOpen}
          setOpen={setIsImportFromGitModalOpen}
        />
      )}
      {isCreatePipelineModalOpen && (
        <CreatePipelineModal
          open={isCreatePipelineModalOpen}
          setOpen={setIsCreatePipelineModalOpen}
          menuGroups={menuGroups}
          showImportViaPipelineButton
          openImportViaPipelineModal={setIsImportViaPipelineModalOpen}
        />
      )}
      {isImportViaPipelineModalOpen && (
        <ImportViaPipelineModal
          open={isImportViaPipelineModalOpen}
          setOpen={setIsImportViaPipelineModalOpen}
        />
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <PipelineFilter
            setSearchText={setSearchText}
            selectedPipelineType={selectedPipelineType}
            setSelectedPipelineType={setSelectedPipelineType}
            setUserNameText={setUserNameText}
            userNameText={userNameText}
            searchText={searchText}
            setPage={setPage}
            setPageSize={setPageSize}
          />
        </div>
        <div className="pt-6">
          <ViewModeToggle mode={viewMode} onChange={setViewMode} />
        </div>
      </div>
      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 py-6 sm:grid-cols-2 lg:grid-cols-3">
          <SkeletonLoader count={12} />
        </div>
      ) : isError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <>
          {viewMode === "list" ? (
            <PipelineTable pipelines={pipelinesList?.data || []} />
          ) : (
            <PipelineGlossary pipelines={pipelinesList?.data || []} />
          )}
          {pipelinesList?.total > 0 && (
            <IndependentPagination
              data={pipelinesList?.data || []}
              page={page}
              pageSizes={pageSizes}
              pageSize={pageSize}
              setPage={setPage}
              setPageSize={setPageSize}
              total={pipelinesList?.total || 0}
              itemLabel={{ singular: "pipeline", plural: "pipelines" }}
            />
          )}
        </>
      )}
    </main>
  );
}
